import { CtxNavRail, CtxNavRailDrawer, CtxNavRailItem, INavigationItem, IToggleFavoriteParams } from '@cscglobal-tbs/nav-rail-ui';
import { LoadingSkeleton } from '@cscglobal-tbs/react-components-core';
import * as React from 'react';
import { useGetFavoriteItems, usePutNavigationPreferenceItem } from '../../api/navigation/navigation';
import { useMruData } from '../../hooks/navBar/useMruData';
import { useNavigationData } from '../../hooks/navBar/useNavigationData';
import { useSystemYear } from '../../hooks/useSystemYear';

export interface INavbarProps {
    containerRef?: React.RefObject<HTMLDivElement>;
}
export const Navbar: React.FunctionComponent<INavbarProps> = ({ containerRef }) => {
    const systemYear = useSystemYear();
    const shouldFetchFavorites = systemYear !== null && systemYear > 2024;

    const { isNavItemsLoading, transformedNavItems, calcReviewPageId } = useNavigationData();
    const { isSearchableNavEnabled, isMruSearchLoading, refetchMruSearches, mruItemsIds, putNavigationSearch, deleteRecentSearches } =
        useMruData();

    const {
        data: favoritesData,
        isLoading: isFavoritesLoading,
        refetch: refetchFavorites,
    } = useGetFavoriteItems({
        query: {
            enabled: shouldFetchFavorites,
        },
    });
    const { mutateAsync: addNewFavorite } = usePutNavigationPreferenceItem();

    const isAnythingLoading =
        isNavItemsLoading || (isSearchableNavEnabled && isMruSearchLoading) || (shouldFetchFavorites && isFavoritesLoading);

    if (isAnythingLoading) return <LoadingSkeleton type='navrail' />;

    const handleToggleFavorite = async (params: IToggleFavoriteParams) => {
        await addNewFavorite(
            {
                data: {
                    id: params.itemId,
                    isPinned: !params.isInFavorites,
                },
            },
            {
                onSuccess: () => {
                    refetchFavorites();
                },
            }
        );
    };

    const handleClearRecentSearchList = async () => {
        await deleteRecentSearches(undefined, {
            onSuccess: () => {
                // so the search panel reflects the cleared state
                refetchMruSearches();
            },
        });
    };

    const handleDrawerSearchItemSelected = async (item: INavigationItem) => {
        await putNavigationSearch(
            { navigationViewId: item.id },
            {
                onSuccess: () => {
                    refetchMruSearches();
                },
            }
        );
    };
    return (
        <CtxNavRail
            mruIds={mruItemsIds}
            favoriteItems={favoritesData?.map((item) => ({
                id: item.id,
                displayName: item.displayName,
                iconName: item.iconName ?? undefined,
                breadcrumb: item.breadcrumb,
                routeUrl: item.routeUrl,
            }))}
            onDrawerSearchItemSelected={handleDrawerSearchItemSelected}
            onClearRecentSearchList={handleClearRecentSearchList}
            onToggleFavorite={handleToggleFavorite}
            containerRef={{ current: containerRef?.current ?? null }}
            currentPageId={calcReviewPageId}
        >
            <>
                {isSearchableNavEnabled && (
                    <CtxNavRailItem
                        item={{ id: 'search', displayName: 'Search', iconName: 'search' }}
                        drawerType='search'
                        searchItems={transformedNavItems}
                    />
                )}
                {shouldFetchFavorites && (
                    <CtxNavRailItem item={{ id: 'favorites', displayName: 'Favorites', iconName: 'favorites' }} drawerType='favorites' />
                )}
                {transformedNavItems.map((item) => (
                    <CtxNavRailItem key={item.id} item={item}>
                        <CtxNavRailDrawer id={item.id} displayName={item.displayName} item={item} />
                    </CtxNavRailItem>
                ))}
            </>
        </CtxNavRail>
    );
};
